import { useEffect, useMemo, useState } from "react";

import { useIntl } from "react-intl";

import useDecodedParams from "Hooks/useDecodedParams";
import { currentDeploymentSelector } from "Reducers/environment/deployment";
import { getDeploymentServices } from "Reducers/environment/deployment/hooks";
import { selectFeatureUsageBasedBillingEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { useAppSelector } from "Store/hooks";

import useGetGitBlobContent from "./useGetGitBlobContent";
import useGetGitCommit from "./useGetGitCommit";
import useGetGitTree from "./useGetGitTree";

import type { Environment } from "@packages/client";
import type { Deployment } from "Api/organizations/projects/environments/deployments/types";

const useServiceFilePath = (deployment?: Deployment, appName: string = "") => {
  const usageBasedBillingEnabled = useAppSelector(
    selectFeatureUsageBasedBillingEnabled
  );
  const intl = useIntl();
  const configFolder = intl.formatMessage({ id: "configuration_folder" });
  if (usageBasedBillingEnabled)
    return { path: configFolder.concat("/config.yaml") };

  const deploymentServices = getDeploymentServices(deployment);
  const service = deploymentServices?.[appName];

  const serviceFilePath = {
    service: "/services.yaml",
    app: "/applications.yaml",
    routes: "/routes.yaml"
  };

  const serviceKind = service?.kind || "routes";
  const path = configFolder.concat(serviceFilePath[serviceKind]);
  return { path, kind: service?.kind };
};

type ServiceAppBlobType = {
  appName?: string;
  environment?: Environment;
};

export const useGetServiceAppBlob = ({
  appName = "",
  environment
}: ServiceAppBlobType) => {
  const projectId = environment?.project;
  const intl = useIntl();
  const { organizationId } = useDecodedParams<{ organizationId: string }>();
  const configFolder = intl.formatMessage({ id: "configuration_folder" });
  const deployment = useAppSelector(state =>
    currentDeploymentSelector(state, {
      organizationId,
      projectId,
      environmentId: environment?.id
    })
  );

  const { path: serviceFilePath, kind } = useServiceFilePath(
    deployment,
    appName
  );

  const [treeCommit, setTreeCommit] = useState<string | null>();
  const [servicePath, setServicePath] = useState<string[]>([]);

  const gitCommit = useGetGitCommit({
    projectId,
    envHeadCommit: environment?.head_commit
  });
  const gitTree = useGetGitTree({
    projectId,
    commitId: treeCommit || gitCommit.data?.tree
  });
  const currentTree = gitTree.data;

  useEffect(() => {
    const path = serviceFilePath?.split("/") || [];
    setServicePath(path);
    setTreeCommit(null);
  }, [serviceFilePath]);

  const blobContent = useMemo(() => {
    for (const path of servicePath) {
      const file = currentTree?.tree.find(obj => {
        return kind === "app" && obj?.path
          ? [
              path,
              //we can also have configurations as .folder.app.yaml
              configFolder.concat(".app.yaml")
            ].includes(obj.path)
          : path && obj?.path === path;
      });

      if (file?.type === "tree" && serviceFilePath.length) {
        setTreeCommit(file.sha);
        setServicePath(servicePath.slice(1));
        return;
      }
      if (file?.type === "blob") {
        return file;
      }
    }
  }, [
    configFolder,
    currentTree?.tree,
    kind,
    serviceFilePath.length,
    servicePath
  ]);

  const blob = useGetGitBlobContent(blobContent);
  const isLoading = blob.isLoading || gitCommit.isLoading || gitTree.isLoading;

  return {
    ...blob,
    isLoading,
    data: {
      data: blob.data,
      currentServiceFilePath: serviceFilePath
    }
  };
};
